import React, { useEffect } from 'react';
import { CalendarClock, Wrench, X } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';

const MaintenanceNoticeModal = ({ isOpen, notice, onClose }) => {
  const ui = useUIColors();

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !notice) return null;

  const accent = ui.warning || '#d97706';
  const schedule = String(notice.schedule || '').trim();

  return (
    <div
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 12000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(15, 23, 42, 0.45)',
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="maintenance-notice-title"
        style={{
          width: 460,
          maxWidth: '100%',
          borderRadius: 14,
          border: `1px solid ${ui.border}`,
          background: ui.panel,
          color: ui.text,
          boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.35)',
          overflow: 'hidden',
        }}
      >
        <div style={{ height: 4, background: accent }} />
        <div style={{ padding: '18px 20px 6px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span
            style={{
              width: 38,
              height: 38,
              flexShrink: 0,
              borderRadius: 10,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `color-mix(in srgb, ${accent} 16%, ${ui.panel})`,
              color: accent,
            }}
          >
            <Wrench size={19} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: accent,
              }}
            >
              Aviso de mantención
            </div>
            <h2
              id="maintenance-notice-title"
              style={{ margin: '4px 0 0', fontSize: 17, fontWeight: 750, lineHeight: 1.3, color: ui.text }}
            >
              {notice.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar aviso"
            style={{
              border: 'none',
              background: 'transparent',
              color: ui.muted,
              cursor: 'pointer',
              padding: 4,
              borderRadius: 6,
              display: 'inline-flex',
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '8px 20px 4px 70px' }}>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: ui.textSecondary, whiteSpace: 'pre-line' }}>
            {notice.message}
          </p>
          {schedule ? (
            <div
              style={{
                marginTop: 12,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '7px 10px',
                borderRadius: 8,
                border: `1px solid color-mix(in srgb, ${accent} 35%, ${ui.border})`,
                background: `color-mix(in srgb, ${accent} 8%, ${ui.panel})`,
                fontSize: 12,
                fontWeight: 650,
                color: ui.text,
              }}
            >
              <CalendarClock size={14} color={accent} />
              {schedule}
            </div>
          ) : null}
        </div>

        <div
          style={{
            marginTop: 16,
            padding: '12px 20px',
            borderTop: `1px solid ${ui.border}`,
            background: ui.header,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            autoFocus
            style={{
              height: 34,
              borderRadius: 8,
              border: 'none',
              background: ui.interactive,
              color: '#fff',
              fontWeight: 700,
              fontSize: 13,
              padding: '0 16px',
              cursor: 'pointer',
            }}
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceNoticeModal;
